// The crew's offline queue (TZ 8, 15.3; STAGE_5 5.6).
//
// In a dead zone nothing is lost: every action the doctor takes is written to IndexedDB
// in order, with an id of its own, and sent as soon as there is a network - in that same
// order, because "on scene" after "departed" is the only order that makes sense. The
// server answers a repeated id with the first answer it gave (X-Client-Action-Id), so a
// retry never moves a status twice or takes money twice.
//
// A refused action (the call was taken from the crew while they were out of reach) is
// not thrown away: it is marked with what the server said and stays in the list for the
// doctor to see.

import { openDB, type IDBPDatabase } from 'idb';

import { ApiError, NetworkError, api, type StoredFile } from '@/api/client';

const DB_NAME = 'sos-crew';
const VERSION = 1;
const ACTIONS = 'actions';
/** Tokens of files already sent, waiting for the action that refers to them. */
const TOKENS = 'tokens';

export type ActionStatus = 'waiting' | 'rejected';

export interface QueuedAction {
  seq: number;
  /** The id the server knows this action by; a replay carries the same one. */
  id: string;
  method: 'post' | 'put';
  path: string;
  pathParams?: Record<string, string | number>;
  body?: unknown;
  /** A photo or a signature: the body is the file itself. */
  file?: Blob;
  purpose?: 'signature' | 'receipt' | 'incident';
  /** This action waits for the token of the file queued under that number. */
  tokenFrom?: number;
  tokenField?: string;
  /** The same for a field that holds several of them: an incident's photos (TZ 9). */
  photosFrom?: number[];
  photosField?: string;
  /** What the doctor did, in their words, for the list of what is waiting. */
  label: string;
  status: ActionStatus;
  error?: string;
  createdAt: number;
}

export type NewAction = Omit<QueuedAction, 'seq' | 'status' | 'createdAt'>;

/** What the queue needs of a store. IndexedDB keeps it across a reload and a dead
 * battery; where the browser refuses one (a private window, storage switched off) the
 * queue still works, for as long as the page is open. */
interface Storage {
  add: (action: Omit<QueuedAction, 'seq'>) => Promise<number>;
  put: (action: QueuedAction) => Promise<void>;
  all: () => Promise<QueuedAction[]>;
  remove: (seq: number) => Promise<void>;
  clear: () => Promise<void>;
  putToken: (seq: number, token: string) => Promise<void>;
  getToken: (seq: number) => Promise<string | undefined>;
  tokenKeys: () => Promise<number[]>;
  removeToken: (seq: number) => Promise<void>;
}

function indexedStorage(): Storage {
  let database: Promise<IDBPDatabase> | null = null;
  const db = () => {
    database ??= openDB(DB_NAME, VERSION, {
      upgrade(open) {
        open.createObjectStore(ACTIONS, { keyPath: 'seq', autoIncrement: true });
        open.createObjectStore(TOKENS);
      },
    });
    return database;
  };
  return {
    add: async (action) => Number(await (await db()).add(ACTIONS, action)),
    put: async (action) => {
      await (await db()).put(ACTIONS, action);
    },
    all: async () => (await (await db()).getAll(ACTIONS)) as QueuedAction[],
    remove: async (seq) => {
      await (await db()).delete(ACTIONS, seq);
    },
    clear: async () => {
      const open = await db();
      await open.clear(ACTIONS);
      await open.clear(TOKENS);
    },
    putToken: async (seq, token) => {
      await (await db()).put(TOKENS, token, seq);
    },
    getToken: async (seq) => (await (await db()).get(TOKENS, seq)) as string | undefined,
    tokenKeys: async () => ((await (await db()).getAllKeys(TOKENS)) as number[]).map(Number),
    removeToken: async (seq) => {
      await (await db()).delete(TOKENS, seq);
    },
  };
}

function memoryStorage(): Storage {
  const actions = new Map<number, QueuedAction>();
  const tokens = new Map<number, string>();
  let last = 0;
  return {
    add: (action) => {
      last += 1;
      actions.set(last, { ...action, seq: last });
      return Promise.resolve(last);
    },
    put: (action) => {
      actions.set(action.seq, action);
      return Promise.resolve();
    },
    all: () => Promise.resolve([...actions.values()]),
    remove: (seq) => {
      actions.delete(seq);
      return Promise.resolve();
    },
    clear: () => {
      actions.clear();
      tokens.clear();
      return Promise.resolve();
    },
    putToken: (seq, token) => {
      tokens.set(seq, token);
      return Promise.resolve();
    },
    getToken: (seq) => Promise.resolve(tokens.get(seq)),
    tokenKeys: () => Promise.resolve([...tokens.keys()]),
    removeToken: (seq) => {
      tokens.delete(seq);
      return Promise.resolve();
    },
  };
}

const store: Storage = 'indexedDB' in globalThis ? indexedStorage() : memoryStorage();

const listeners = new Set<() => void>();
let snapshot: QueuedAction[] = [];

function announce(actions: QueuedAction[]): void {
  snapshot = actions;
  listeners.forEach((listener) => {
    listener();
  });
}

async function all(): Promise<QueuedAction[]> {
  return (await store.all()).sort((a, b) => a.seq - b.seq);
}

async function refresh(): Promise<QueuedAction[]> {
  const actions = await all();
  announce(actions);
  return actions;
}

/** Put an action in the queue; it goes out when the network is back. */
export async function enqueue(action: NewAction): Promise<number> {
  const seq = await store.add({ ...action, status: 'waiting', createdAt: Date.now() });
  await refresh();
  return seq;
}

export async function forget(seq: number): Promise<void> {
  await store.remove(seq);
  await refresh();
}

export async function clear(): Promise<void> {
  await store.clear();
  await refresh();
}

// --- sending ------------------------------------------------------------------------------

async function send(action: QueuedAction): Promise<unknown> {
  if (action.file) {
    const stored = await api.uploadFile(action.file, action.purpose ?? 'receipt', action.id);
    return stored;
  }
  const options = {
    ...(action.pathParams ? { path: action.pathParams } : {}),
    ...(action.body === undefined ? {} : { body: action.body }),
    actionId: action.id,
  } as never;
  return action.method === 'put'
    ? api.put(action.path as never, options)
    : api.post(action.path as never, options);
}

/** The body with the tokens of the files this action waited for. */
async function withToken(action: QueuedAction): Promise<QueuedAction> {
  let body = action.body as Record<string, unknown> | undefined;
  if (action.tokenFrom !== undefined && action.tokenField) {
    const token = await store.getToken(action.tokenFrom);
    if (token !== undefined) body = { ...body, [action.tokenField]: token };
  }
  if (action.photosFrom?.length && action.photosField) {
    const kept = Array.isArray(body?.[action.photosField])
      ? (body[action.photosField] as string[])
      : [];
    const added = (
      await Promise.all(action.photosFrom.map((seq) => store.getToken(seq)))
    ).filter((token): token is string => token !== undefined);
    body = { ...body, [action.photosField]: [...kept, ...added] };
  }
  return body === action.body ? action : { ...action, body };
}

let flushing: Promise<number> | null = null;

/** Send what is waiting, oldest first. Stops at the first action the network swallows;
 * a refused one is marked and the queue goes on with the next. */
/** Send what waits, in order; resolves to how many actions went out (the screens then
 * fetch again: what they asked for on reconnecting may predate the actions). */
export function flush(): Promise<number> {
  flushing ??= run().finally(() => {
    flushing = null;
  });
  return flushing;
}

async function run(): Promise<number> {
  let sent = 0;
  for (const queued of await all()) {
    if (queued.status !== 'waiting') continue;
    const action = await withToken(queued);
    try {
      const result = await send(action);
      if (action.file) await store.putToken(action.seq, (result as StoredFile).token);
      await store.remove(action.seq);
      sent += 1;
    } catch (error) {
      if (error instanceof NetworkError) {
        await refresh();
        return sent;
      }
      const reason = error instanceof ApiError ? refusal(error) : String(error);
      await store.put({ ...queued, status: 'rejected', error: reason });
    }
  }
  // Tokens of files whose action has gone are of no use to anybody.
  const left = await refresh();
  const waited = new Set([
    ...left.map((item) => item.tokenFrom),
    ...left.flatMap((item) => item.photosFrom ?? []),
  ]);
  for (const key of await store.tokenKeys()) {
    if (!waited.has(key)) await store.removeToken(key);
  }
  return sent;
}

function refusal(error: ApiError): string {
  const detail = error.detail as { message?: unknown; code?: unknown } | null;
  if (detail && typeof detail.code === 'string') return detail.code;
  return String(error.status);
}

// --- what the screens read ------------------------------------------------------------------

export const queueStore = {
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    void refresh();
    return () => {
      listeners.delete(listener);
    };
  },
  actions: (): QueuedAction[] => snapshot,
  waiting: (): number => snapshot.filter((action) => action.status === 'waiting').length,
  rejected: (): QueuedAction[] => snapshot.filter((action) => action.status === 'rejected'),
};
